import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, api, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import SectionHeading from "../GRI300/GRI308/SectionHeading";

const Economic28 = ({ hide, downloadMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    employeeWorkersTotal: {
      commonTypeEmployees: "",
      performForOrganization: "",
      controllerByOrganization: "",
    },

    methodologiesAndAssumptions: {
      projectAssignments: "",
      FTEReporting: "",
      anotherTimeFrame: "",
    },

    employeeWorkerFluctuations: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(
          `${api}/gri/get?type=GeneralDisclosures2021&Id=${id}`
        );
        console.log(response.data, "resp");
        const data =
          response.data.data.GeneralDisclosures2021?.nonEmployeeWorkers || {};
        if (response?.data?.data?.year < currentYear - 1) setDisabled(true);

        setFormData({
          employeeWorkersTotal: {
            commonTypeEmployees:
              data.employeeWorkersTotal?.commonTypeEmployees || "",
            controllerByOrganization:
              data.employeeWorkersTotal?.controllerByOrganization || "",
            performForOrganization:
              data.employeeWorkersTotal?.performForOrganization || "",
          },
          methodologiesAndAssumptions: {
            FTEReporting: data.methodologiesAndAssumptions?.FTEReporting || "",
            anotherTimeFrame:
              data.methodologiesAndAssumptions?.anotherTimeFrame || "",
            projectAssignments:
              data.methodologiesAndAssumptions?.projectAssignments || "",
          },
          employeeWorkerFluctuations: data.employeeWorkerFluctuations || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData
    if (!formData.employeeWorkersTotal.commonTypeEmployees) {
      newErrors.commonTypeEmployees = "This field is required";
      formIsValid = false;
    }

    if (!formData.employeeWorkersTotal.performForOrganization) {
      newErrors.performForOrganization = "This field is required";
      formIsValid = false;
    }

    if (!formData.employeeWorkersTotal.controllerByOrganization) {
      newErrors.controllerByOrganization = "This field is required";
      formIsValid = false;
    }

    //sectionB
    if (!formData.methodologiesAndAssumptions.projectAssignments) {
      newErrors.projectAssignments = "This field is required";
      formIsValid = false;
    }

    if (!formData.methodologiesAndAssumptions.anotherTimeFrame) {
      newErrors.anotherTimeFrame = "This field is required";
      formIsValid = false;
    }

    if (!formData.methodologiesAndAssumptions.FTEReporting) {
      newErrors.FTEReporting = "This field is required";
      formIsValid = false;
    }

    if (!formData.employeeWorkerFluctuations) {
      newErrors.employeeWorkerFluctuations = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) return; // Validate form before submission

      console.log("Form Data Submitted:", formData);

      const payload = {
        type: "GeneralDisclosures2021",
        nonEmployeeWorkers: {
          ...formData,
        },
        submitted2_8: true,
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/general-disclosure/2-9/${id}`);
      } catch (error) {
        console.error("Error updating data:", error);
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/general-disclosure/2-9/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {/* <SectionHeading text="2-1 Activities, value chain and other business relationships" /> */}
      <p className={styles.main_heading}>2-8 Workers who are not employees</p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}
        <SectionHeading
          text="a. Provide the total number of workers who are not employees and whose work is controlled by the organization."
          sx={{ marginTop: hide ? "-1rem" : "0rem" }}
        />
        <CustomTextField
          value={formData.employeeWorkersTotal.controllerByOrganization}
          // onChange={(e) => handleNestedInputChange("controllerByOrganization", e.target.value)}
          onChange={(e) =>
            handleNestedInputChange(
              "employeeWorkersTotal",
              "controllerByOrganization",
              e.target.value
            )
          }
          error={errors.controllerByOrganization}
          helperText={errors.controllerByOrganization}
          disabled={disabled}
        />
        <Box sx={{ marginLeft: "1.1rem" }}>
          <SectionHeading
            text="i. Briefly describe the most common type of non-employee worker e.g. independent
       contractors, consultants, freelancers etc. and explain how the organization contracts with these workers (e.g., written contracts, service agreements)"
          />
          <CustomTextField
            value={formData.employeeWorkersTotal.commonTypeEmployees}
            onChange={(e) =>
              handleNestedInputChange(
                "employeeWorkersTotal",
                "commonTypeEmployees",
                e.target.value
              )
            }
            error={errors.commonTypeEmployees}
            helperText={errors.commonTypeEmployees}
            disabled={disabled}
          />

          <SectionHeading text="ii. Briefly describe the types of work these non-employee workers perform for the organization. " />
          <CustomTextField
            value={formData.employeeWorkersTotal.performForOrganization}
            onChange={(e) =>
              handleNestedInputChange(
                "employeeWorkersTotal",
                "performForOrganization",
                e.target.value
              )
            }
            error={errors.performForOrganization}
            helperText={errors.performForOrganization}
            disabled={disabled}
          />
        </Box>

        {/* Section B */}
        <SectionHeading
          text="b. Describe the methodologies and assumptions used by the organization to identifies workers who are not employees but whose work is controlled by the organization through contracts, project assignments etc"
          sx={{ marginTop: "2rem" }}
        />
        <CustomTextField
          value={formData.methodologiesAndAssumptions.projectAssignments}
          onChange={(e) =>
            handleNestedInputChange(
              "methodologiesAndAssumptions",
              "projectAssignments",
              e.target.value
            )
          }
          error={errors.projectAssignments}
          helperText={errors.projectAssignments}
          disabled={disabled}
        />
        <Box sx={{ marginLeft: "1.1rem" }}>
          <SectionHeading text="i. If FTE is used for reporting non-employee workers in headcount (total number of individuals) or Full-Time Equivalent (FTE), explain the methodology for calculating FTE from non-employee workers." />
          <CustomTextField
            value={formData.methodologiesAndAssumptions.FTEReporting}
            onChange={(e) =>
              handleNestedInputChange(
                "methodologiesAndAssumptions",
                "FTEReporting",
                e.target.value
              )
            }
            error={errors.FTEReporting}
            helperText={errors.FTEReporting}
            disabled={disabled}
          />

          <SectionHeading text="ii. Describe if the data represent the number of non-employee workers at the end of the reporting period, the average number of employees throughout the period, or another timeframe? " />
          <CustomTextField
            value={formData.methodologiesAndAssumptions.anotherTimeFrame}
            onChange={(e) =>
              handleNestedInputChange(
                "methodologiesAndAssumptions",
                "anotherTimeFrame",
                e.target.value
              )
            }
            error={errors.anotherTimeFrame}
            helperText={errors.anotherTimeFrame}
            disabled={disabled}
          />
        </Box>

        {/* Section C */}
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <SectionHeading
          text="c. Describe the significant fluctuations in the number of non-employee workers during the reporting period and between reporting periods within the organization."
          sx={{ marginTop: "2rem" }}
        />
        <CustomTextField
          value={formData.employeeWorkerFluctuations}
          onChange={(e) =>
            handleInputChange("employeeWorkerFluctuations", e.target.value)
          }
          error={errors.employeeWorkerFluctuations}
          helperText={errors.employeeWorkerFluctuations}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default Economic28;
